import React, { FC, useMemo } from "react";
import { useParams } from "react-router";
import { Spinner } from "@lookiero/aurora";
import { QueryStatus } from "@lookiero/messaging-react";
import { Layout as UiLayout } from "@lookiero/sty-psp-ui";
import { CheckoutItemProjection } from "../../../../projection/checkoutItem/checkoutItem";
import { useViewFirstAvailableCheckoutByCustomerId } from "../../../projection/checkout/react/useViewFirstAvailableCheckoutByCustomerId";
import { ReturnQuestionFeedbackProvider } from "../../components/organisms/returnQuestions/behaviors/useReturnQuestionFeedback";
import { useReturnQuestionFeedbackDraft } from "../../components/organisms/returnQuestions/behaviors/useReturnQuestionFeedbackDraft";
import { useStaticInfo } from "../../hooks/useStaticInfo";
import { ReturnQuestionsForm } from "./components/returnQuestionsForm/ReturnQuestionsForm";

interface ReturnProps {
  readonly layout: UiLayout;
}

const Return: FC<ReturnProps> = ({ layout }) => {
  const { id } = useParams();
  const {
    customer: { customerId, country, segment },
  } = useStaticInfo();

  const [checkout, checkoutStatus] = useViewFirstAvailableCheckoutByCustomerId({ customerId });

  const checkoutItem = checkout?.items.find((checkoutItem) => checkoutItem.id === id) as CheckoutItemProjection;

  const { draft, loaded: draftLoaded, persist, clear } = useReturnQuestionFeedbackDraft({ checkoutItemId: id });

  const feedback = useMemo(() => ({ ...(checkoutItem?.feedbacks || {}), ...draft }), [checkoutItem?.feedbacks, draft]);

  const dependenciesLoaded = checkoutStatus !== QueryStatus.LOADING && checkout && checkoutItem && draftLoaded;

  if (!dependenciesLoaded) {
    return <Spinner testID="spinner" />;
  }

  return (
    <ReturnQuestionFeedbackProvider key={checkoutItem.id} feedback={feedback} onFeedbackChange={persist}>
      <ReturnQuestionsForm
        checkout={checkout}
        checkoutItem={checkoutItem}
        country={country}
        layout={layout}
        onClearDraft={clear}
        segment={segment}
      />
    </ReturnQuestionFeedbackProvider>
  );
};

export { Return };
